(() => {
  const ROOT_SELECTOR =
    '.fw-contact-page[data-fw-contact-rail-page]';

  const RAIL_SELECTOR =
    '.fw-contact-info';

  const FORM_SELECTOR =
    '.fw-contact-form';

  const HEADER_SELECTOR =
    '.fw-header';

  const TRACK_CLASS =
    'fw-contact-info__track';

  const VIEWPORT_CLASS =
    'fw-contact-info__viewport';

  const READY_CLASS =
    'has-fw-contact-info-rail';

  const desktopQuery =
    window.matchMedia(
      '(min-width: 991px)'
    );

  const HEADER_GAP = 24;
  const VIEWPORT_BOTTOM_GAP = 24;

  const clamp = (
    value,
    min,
    max
  ) => {
    return Math.min(
      Math.max(
        value,
        min
      ),
      max
    );
  };

  const getScrollY = () => {
    return (
      window.scrollY ||
      document.documentElement.scrollTop ||
      0
    );
  };

  const initRoot = (root) => {
    if (
      !root ||
      root.dataset.fwContactRailReady ===
        'true'
    ) {
      return;
    }

    const rail =
      root.querySelector(
        RAIL_SELECTOR
      );

    const form =
      root.querySelector(
        FORM_SELECTOR
      );

    const header =
      document.querySelector(
        HEADER_SELECTOR
      );

    if (!rail) {
      return;
    }

    const track =
      document.createElement(
        'div'
      );

    track.className =
      TRACK_CLASS;

    track.setAttribute(
      'data-fw-contact-info-track',
      ''
    );

    const viewport =
      document.createElement(
        'div'
      );

    viewport.className =
      VIEWPORT_CLASS;

    viewport.setAttribute(
      'data-fw-contact-info-viewport',
      ''
    );

    rail.before(
      track
    );

    track.appendChild(
      viewport
    );

    viewport.appendChild(
      rail
    );

    root.dataset.fwContactRailReady =
      'true';

    root.classList.add(
      READY_CLASS
    );

    let frame = null;

    const isDesktop = () => {
      return desktopQuery.matches;
    };

    const clearGeometry = () => {
      root.style.removeProperty(
        '--fw-contact-rail-sticky-top'
      );

      root.style.removeProperty(
        '--fw-contact-rail-viewport-height'
      );

      root.style.removeProperty(
        '--fw-contact-rail-left'
      );

      root.style.removeProperty(
        '--fw-contact-rail-width'
      );

      root.style.removeProperty(
        '--fw-contact-rail-offset'
      );

      track.style.removeProperty(
        'min-height'
      );

      viewport.style.removeProperty(
        'overflow'
      );

      viewport.classList.remove(
        'is-virtual',
        'is-active'
      );
    };

    const render = () => {
      frame = null;

      if (!isDesktop()) {
        clearGeometry();
        return;
      }

      const scrollY =
        getScrollY();

      const headerBottom =
        header
          ? Math.max(
              0,
              header
                .getBoundingClientRect()
                .bottom
            )
          : 0;

      const stickyTop =
        headerBottom +
        HEADER_GAP;

      const availableHeight =
        Math.max(
          160,
          window.innerHeight -
            stickyTop -
            VIEWPORT_BOTTOM_GAP
        );

      const railHeight =
        rail.scrollHeight;

      const viewportHeight =
        Math.min(
          railHeight,
          availableHeight
        );

      const maxOffset =
        Math.max(
          0,
          railHeight -
            viewportHeight
        );

      root.style.setProperty(
        '--fw-contact-rail-sticky-top',
        `${stickyTop.toFixed(2)}px`
      );

      root.style.setProperty(
        '--fw-contact-rail-viewport-height',
        `${viewportHeight.toFixed(2)}px`
      );

      /*
       * The track must always reserve enough document height
       * for the complete rail.
       *
       * If the form is taller, normal grid/flex stretching can
       * make the track taller than this minimum.
       */
      track.style.minHeight =
        `${Math.ceil(railHeight)}px`;

      /*
       * The rail itself may be taller than the visible sticky
       * viewport. Clip the unrevealed portion instead of letting
       * it visually escape into the footer.
       */
      viewport.style.overflow =
        'hidden';

      /*
       * The previous implementation promoted the viewport to
       * position: fixed. That allowed the rail to escape the
       * section boundary.
       *
       * These classes are deliberately kept off. The viewport
       * remains position: sticky and is therefore constrained by
       * the track.
       */
      viewport.classList.remove(
        'is-virtual',
        'is-active'
      );

      const trackRect =
        track.getBoundingClientRect();

      const trackDocumentTop =
        scrollY +
        trackRect.top;

      const stickyStart =
        Math.max(
          0,
          trackDocumentTop -
            stickyTop
        );

      const stickyTravel =
        Math.max(
          0,
          trackRect.height -
            viewportHeight
        );

      let progress = 0;

      if (
        maxOffset > 0 &&
        stickyTravel > 0
      ) {
        progress =
          clamp(
            (
              scrollY -
              stickyStart
            ) /
              stickyTravel,
            0,
            1
          );
      }

      const offset =
        maxOffset *
        progress;

      root.style.setProperty(
        '--fw-contact-rail-offset',
        `${offset.toFixed(2)}px`
      );
    };

    const scheduleRender = () => {
      if (
        frame !== null
      ) {
        return;
      }

      frame =
        window.requestAnimationFrame(
          render
        );
    };

    const handleResize = () => {
      scheduleRender();
    };

    window.addEventListener(
      'scroll',
      scheduleRender,
      {
        passive: true
      }
    );

    window.addEventListener(
      'resize',
      handleResize
    );

    desktopQuery.addEventListener?.(
      'change',
      handleResize
    );

    if (
      'ResizeObserver' in
      window
    ) {
      const resizeObserver =
        new ResizeObserver(
          scheduleRender
        );

      resizeObserver.observe(
        rail
      );

      resizeObserver.observe(
        track
      );

      if (form) {
        resizeObserver.observe(
          form
        );
      }

      if (header) {
        resizeObserver.observe(
          header
        );
      }
    }

    if (
      document.fonts &&
      document.fonts.ready
    ) {
      document.fonts.ready.then(
        scheduleRender
      );
    }

    window.addEventListener(
      'load',
      scheduleRender,
      {
        once: true
      }
    );

    scheduleRender();
  };

  const initWithin = (
    scope = document
  ) => {
    if (
      scope.matches?.(
        ROOT_SELECTOR
      )
    ) {
      initRoot(
        scope
      );
    }

    scope
      .querySelectorAll?.(
        ROOT_SELECTOR
      )
      .forEach(
        initRoot
      );
  };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initWithin();
      },
      {
        once: true
      }
    );
  } else {
    initWithin();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initWithin(
        event.target
      );
    }
  );
})();